import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import type { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

import instance from 'api';
import { sleep } from 'utils/sleepUtils';
import type { PaginationType } from 'types/filtersType';
import { campaignsMockData, upcomingCampaignsMockData } from '../pages/MessageScheduler/constants';

type BaseResponseType = {
  data: unknown;
  message: string;
  meta: PaginationType;
} & AxiosResponse;

const mockDataArr: {url: string; method: string; data: unknown}[] = [
  {
    url: '/api/users/{userId}/campaigns/',
    method: 'get',
    data: campaignsMockData,
  },
  {
    url: '/api/users/{userId}/campaigns/',
    method: 'post',
    data: campaignsMockData[0],
  },
  {
    url: '/api/users/{userId}/campaigns/{campaignId}/',
    method: 'put',
    data: campaignsMockData[0],
  },
  {
    url: '/api/users/{userId}/campaigns/{campaignId}/',
    method: 'delete',
    data: undefined,
  },
  {
    url: '/api/users/{userId}/upcoming-campaigns/',
    method: 'get',
    data: upcomingCampaignsMockData,
  },
];

export const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
      responseType?: AxiosRequestConfig['responseType'];
    },
    unknown,
    unknown
  > => async ({ url, method, data, params, headers, responseType = 'json' }) => {
    try {
      const mockResult = mockDataArr.find((item) => item.url === url && item.method === method);
      if (mockResult) {
        await sleep(2000);
        return {
          data: mockResult.data,
        } as unknown as MaybePromise<QueryReturnValue<unknown, unknown, object>>;
      }

      const result: BaseResponseType = await instance({
        url,
        method,
        data,
        params,
        headers,
        responseType,
      });
      if (url.includes('user-csv')) {
        return { data: result };
      }

      return { data: result.data, meta: result.meta };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
